/**
 * CoachPlaybook - JerseyIcon Component
 * Version: 0.5.145
 * Description: Customizable basketball jersey SVG with positioning controls
 * 
 * v0.5.121 Changes:
 * - Added logo positioning props (logoY, logoSize, logoRotation, logoFlipX, logoFlipY)
 * - Logo renders with transform group for rotation and flip
 * - Added logo defaults to DEFAULT_JERSEY_TEMPLATE
 */

import React from 'react';

// Available font options - mix of system and Google Fonts
export const JERSEY_FONTS = [
  // Bold Athletic Fonts
  { id: 'impact', name: 'Impact', family: 'Impact, Haettenschweiler, sans-serif', category: 'Athletic' },
  { id: 'arial-black', name: 'Arial Black', family: '"Arial Black", Gadget, sans-serif', category: 'Athletic' },
  { id: 'bebas', name: 'Bebas Neue', family: '"Bebas Neue", sans-serif', googleFont: true, category: 'Athletic' },
  { id: 'oswald', name: 'Oswald', family: 'Oswald, sans-serif', googleFont: true, category: 'Athletic' },
  { id: 'teko', name: 'Teko', family: 'Teko, sans-serif', googleFont: true, category: 'Athletic' },
  { id: 'russo', name: 'Russo One', family: '"Russo One", sans-serif', googleFont: true, category: 'Athletic' },
  
  // Block/Collegiate Fonts
  { id: 'anton', name: 'Anton', family: 'Anton, sans-serif', googleFont: true, category: 'Block' },
  { id: 'black-ops', name: 'Black Ops One', family: '"Black Ops One", cursive', googleFont: true, category: 'Block' },
  { id: 'bungee', name: 'Bungee', family: 'Bungee, cursive', googleFont: true, category: 'Block' },
  
  // Classic Fonts
  { id: 'roboto-cond', name: 'Roboto Condensed', family: '"Roboto Condensed", sans-serif', googleFont: true, category: 'Classic' },
  { id: 'archivo-black', name: 'Archivo Black', family: '"Archivo Black", sans-serif', googleFont: true, category: 'Classic' },
  { id: 'graduate', name: 'Graduate', family: 'Graduate, cursive', googleFont: true, category: 'Collegiate' },
  
  // Street/Graffiti Style
  { id: 'bangers', name: 'Bangers', family: 'Bangers, cursive', googleFont: true, category: 'Street' },
  { id: 'bungee-shade', name: 'Bungee Shade', family: '"Bungee Shade", cursive', googleFont: true, category: 'Street' },
  { id: 'nabla', name: 'Nabla', family: 'Nabla, cursive', googleFont: true, category: 'Street' },
  { id: 'rubik-glitch', name: 'Rubik Glitch', family: '"Rubik Glitch", cursive', googleFont: true, category: 'Street' },
  { id: 'monoton', name: 'Monoton', family: 'Monoton, cursive', googleFont: true, category: 'Street' },
  
  // Marker/Whiteboard Style
  { id: 'permanent-marker', name: 'Permanent Marker', family: '"Permanent Marker", cursive', googleFont: true, category: 'Marker' },
  { id: 'caveat', name: 'Caveat', family: 'Caveat, cursive', googleFont: true, category: 'Marker' },
  { id: 'rock-salt', name: 'Rock Salt', family: '"Rock Salt", cursive', googleFont: true, category: 'Marker' },
  { id: 'architects-daughter', name: 'Architects Daughter', family: '"Architects Daughter", cursive', googleFont: true, category: 'Marker' },
  
  // Script/Brush Style
  { id: 'pacifico', name: 'Pacifico', family: 'Pacifico, cursive', googleFont: true, category: 'Script' },
  { id: 'lobster', name: 'Lobster', family: 'Lobster, cursive', googleFont: true, category: 'Script' },
  { id: 'satisfy', name: 'Satisfy', family: 'Satisfy, cursive', googleFont: true, category: 'Script' }
];

// Get Google Fonts link for loading
export const getGoogleFontsLink = () => {
  const googleFonts = JERSEY_FONTS.filter(f => f.googleFont).map(f => f.name.replace(/ /g, '+'));
  return `https://fonts.googleapis.com/css2?family=${googleFonts.join('&family=')}&display=swap`;
};

// Helper to darken a hex color
const darkenColor = (hex, percent = 20) => {
  if (!hex || hex.charAt(0) !== '#') return '#000000';
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.max(0, (num >> 16) - Math.round(2.55 * percent));
  const g = Math.max(0, ((num >> 8) & 0x00FF) - Math.round(2.55 * percent));
  const b = Math.max(0, (num & 0x0000FF) - Math.round(2.55 * percent));
  return `#${(r << 16 | g << 8 | b).toString(16).padStart(6, '0')}`;
};

// Realistic jersey SVG path from template
export const JERSEY_PATH = "M 202 383 L 200 390 L 212 436 L 216 476 L 212 547 L 203 586 L 188 623 L 174 644 L 162 656 L 146 666 L 147 1201 L 710 1202 L 712 666 L 695 655 L 672 626 L 660 600 L 648 557 L 643 520 L 642 480 L 646 438 L 658 384 L 532 333 L 527 334 L 520 353 L 501 374 L 476 388 L 451 395 L 408 395 L 382 388 L 366 380 L 343 360 L 330 333 Z";

// ViewBox for the jersey
export const JERSEY_VIEWBOX = "140 320 590 900";

// Default positioning values (name above number)
export const DEFAULT_POSITIONS = {
  numberY: 55,      // 0-100 percentage from top (lower on jersey)
  nameY: 5,         // 0-100 percentage from top (higher on jersey - above number)
  numberSize: 100,  // 50-150 percentage scale
  nameSize: 100,    // 50-150 percentage scale
  jerseyBorderWeight: 12,   // Jersey outline thickness (0-24)
  numberBorderWeight: 6,    // Number inner outline thickness (0-16)
  numberBorderWeight2: 0,   // Number outer outline thickness (0-16, 0 = disabled)
  nameBorderWeight: 4,      // Name inner outline thickness (0-12)
  nameBorderWeight2: 0      // Name outer outline thickness (0-12, 0 = disabled)
};

// Border weight limits
export const BORDER_LIMITS = {
  jersey: { min: 0, max: 24 },
  numberInner: { min: 0, max: 16 },
  numberOuter: { min: 0, max: 16 },
  nameInner: { min: 0, max: 12 },
  nameOuter: { min: 0, max: 12 }
};

const JerseyIcon = ({
  size = 100,
  number = '',
  view = 'front',
  teamName = '',
  lastName = '',
  fillColor = '#2563eb',
  outlineColor = null,
  // Legacy props (used as fallbacks)
  numberColor = '#ffffff',
  numberOutline = null,
  numberOutline2 = null,
  textColor = null,
  textOutline = null,
  textOutline2 = null,
  logoUrl = null,
  // Logo positioning controls
  logoY: propLogoY = 65,        // 0-100 percent from top
  logoSize: propLogoSize = 50,  // 10-100 percent scale
  logoRotation = 0,             // -180 to 180 degrees
  logoFlipX = false,            // horizontal flip
  logoFlipY = false,            // vertical flip
  fontFamily = 'Impact, Haettenschweiler, sans-serif',
  numberY: rawNumberY = 55,
  nameY: rawNameY = 5,
  numberSize: rawNumberSize = 100,
  nameSize: rawNameSize = 100,
  jerseyBorderWeight = 12,
  numberBorderWeight = 6,
  numberBorderWeight2 = 0,
  nameBorderWeight = 4,
  nameBorderWeight2 = 0,
  // NEW: Front-specific overrides
  front = null,
  // NEW: Back-specific overrides  
  back = null,
  className = ''
}) => {
  // ROBUST VALIDATION
  const isValidNumber = (val) => typeof val === 'number' && !isNaN(val) && isFinite(val);
  
  // Determine which settings to use based on view
  const viewSettings = view === 'front' ? front : back;
  
  // Get effective values - prefer view-specific, fall back to legacy props
  const effectiveNumberY = isValidNumber(viewSettings?.numberY) ? viewSettings.numberY : 
                           isValidNumber(rawNumberY) ? rawNumberY : DEFAULT_POSITIONS.numberY;
  const effectiveNumberSize = isValidNumber(viewSettings?.numberSize) ? viewSettings.numberSize :
                              isValidNumber(rawNumberSize) ? rawNumberSize : DEFAULT_POSITIONS.numberSize;
  const effectiveTextY = isValidNumber(viewSettings?.textY) ? viewSettings.textY :
                         isValidNumber(rawNameY) ? rawNameY : DEFAULT_POSITIONS.nameY;
  const effectiveTextSize = isValidNumber(viewSettings?.textSize) ? viewSettings.textSize :
                            isValidNumber(rawNameSize) ? rawNameSize : DEFAULT_POSITIONS.nameSize;
  
  // Colors - prefer view-specific
  const effectiveNumberColor = viewSettings?.numberColor || numberColor;
  const effectiveNumberOutline = viewSettings?.numberOutline || numberOutline;
  const effectiveNumberOutline2 = viewSettings?.numberOutline2 ?? numberOutline2;
  const effectiveTextColor = viewSettings?.textColor || textColor || numberColor;
  const effectiveTextOutline = viewSettings?.textOutline || textOutline || numberOutline;
  const effectiveTextOutline2 = viewSettings?.textOutline2 ?? textOutline2;
  
  // Border weights - prefer view-specific
  const effectiveNumberBorderWeight = isValidNumber(viewSettings?.numberBorderWeight) ? viewSettings.numberBorderWeight : numberBorderWeight;
  const effectiveNumberBorderWeight2 = isValidNumber(viewSettings?.numberBorderWeight2) ? viewSettings.numberBorderWeight2 : numberBorderWeight2;
  const effectiveTextBorderWeight = isValidNumber(viewSettings?.textBorderWeight) ? viewSettings.textBorderWeight : nameBorderWeight;
  const effectiveTextBorderWeight2 = isValidNumber(viewSettings?.textBorderWeight2) ? viewSettings.textBorderWeight2 : nameBorderWeight2;
  
  // Font - prefer view-specific
  const effectiveFontFamily = viewSettings?.fontFamily || fontFamily;
  
  // Auto-calculate outline color if not provided
  const strokeColor = outlineColor || darkenColor(fillColor, 25);
  const actualNumberOutline = effectiveNumberOutline || strokeColor;
  const actualNumberOutline2 = effectiveNumberOutline2 || null;
  const actualTextOutline = effectiveTextOutline || strokeColor;
  const actualTextOutline2 = effectiveTextOutline2 || null;
  
  // Height is ~1.5x width for proper jersey proportions
  const height = Math.round(size * 1.5);
  
  // Calculate font sizes based on text length and size multiplier
  const numStr = String(number).slice(0, 3);
  const baseNumberSize = numStr.length === 1 ? 320 : numStr.length === 2 ? 280 : 220;
  const numberFontSize = baseNumberSize * (effectiveNumberSize / 100);
  
  // Team name sizing (front view)
  const teamNameText = teamName.toUpperCase();
  const baseTeamSize = teamNameText.length <= 6 ? 80 : teamNameText.length <= 10 ? 65 : 50;
  const teamNameFontSize = baseTeamSize * (effectiveTextSize / 100);
  
  // Last name sizing (back view)
  const lastNameText = lastName.toUpperCase();
  const baseLastSize = lastNameText.length <= 6 ? 90 : lastNameText.length <= 10 ? 70 : 55;
  const lastNameFontSize = baseLastSize * (effectiveTextSize / 100);
  
  // Positions - map 0-100 percentage to actual Y coordinates
  // Jersey body spans roughly Y 400 to Y 1150 in viewBox
  const minY = 450;
  const maxY = 1100;
  const yRange = maxY - minY;
  
  const centerX = 428;
  const calculatedNumberY = minY + (yRange * (effectiveNumberY / 100));
  const calculatedTextY = minY + (yRange * (effectiveTextY / 100));
  
  // Logo positioning - use props with validation
  const logoYPosition = minY + (yRange * (propLogoY / 100));
  const logoSizeCalc = 36 + (300 * (propLogoSize / 100)); // 36-336px range
  
  // Stroke widths
  const bodyStrokeWidth = jerseyBorderWeight;
  const numberStrokeWidth = effectiveNumberBorderWeight;
  const numberStrokeWidth2 = effectiveNumberBorderWeight2;
  const textStrokeWidth = effectiveTextBorderWeight;
  const textStrokeWidth2 = effectiveTextBorderWeight2;
  
  // Calculate total stroke for outer border (inner + outer)
  const totalNumberStroke = numberStrokeWidth + (numberStrokeWidth2 * 2);
  const totalTextStroke = textStrokeWidth + (textStrokeWidth2 * 2);
  
  return (
    <svg 
      width={size} 
      height={height} 
      viewBox={JERSEY_VIEWBOX}
      className={className}
      aria-label={`Jersey ${view === 'front' ? teamName : lastName} number ${number}`}
    >
      {/* Jersey body */}
      <path 
        d={JERSEY_PATH}
        fill={fillColor}
        stroke={strokeColor}
        strokeWidth={bodyStrokeWidth}
        strokeLinejoin="round"
      />
      
      {/* Front view: Team name and/or logo */}
      {view === 'front' && (
        <>
          {logoUrl && (
            <g transform={`
              translate(${centerX}, ${logoYPosition})
              rotate(${logoRotation})
              scale(${logoFlipX ? -1 : 1}, ${logoFlipY ? -1 : 1})
            `}>
              <image
                href={logoUrl}
                x={-logoSizeCalc / 2}
                y={-logoSizeCalc / 2}
                width={logoSizeCalc}
                height={logoSizeCalc}
                preserveAspectRatio="xMidYMid meet"
              />
            </g>
          )}
          
          {teamName && !logoUrl && (
            <>
              {/* Outer stroke (if enabled) */}
              {textStrokeWidth2 > 0 && actualTextOutline2 && (
                <text
                  x={centerX}
                  y={calculatedTextY}
                  textAnchor="middle"
                  fontSize={teamNameFontSize}
                  fontWeight="bold"
                  fill="transparent"
                  stroke={actualTextOutline2}
                  strokeWidth={totalTextStroke}
                  strokeLinejoin="round"
                  fontFamily={effectiveFontFamily}
                  letterSpacing="2"
                >
                  {teamNameText}
                </text>
              )}
              {/* Inner stroke + fill */}
              <text
                x={centerX}
                y={calculatedTextY}
                textAnchor="middle"
                fontSize={teamNameFontSize}
                fontWeight="bold"
                fill={effectiveTextColor}
                stroke={actualTextOutline}
                strokeWidth={textStrokeWidth}
                paintOrder="stroke"
                fontFamily={effectiveFontFamily}
                letterSpacing="2"
              >
                {teamNameText}
              </text>
            </>
          )}
        </>
      )}
      
      {/* Back view: Player last name */}
      {view === 'back' && lastName && (
        <>
          {/* Outer stroke (if enabled) */}
          {textStrokeWidth2 > 0 && actualTextOutline2 && (
            <text
              x={centerX}
              y={calculatedTextY}
              textAnchor="middle"
              fontSize={lastNameFontSize}
              fontWeight="bold"
              fill="transparent"
              stroke={actualTextOutline2}
              strokeWidth={totalTextStroke}
              strokeLinejoin="round"
              fontFamily={effectiveFontFamily}
              letterSpacing="4"
            >
              {lastNameText}
            </text>
          )}
          {/* Inner stroke + fill */}
          <text
            x={centerX}
            y={calculatedTextY}
            textAnchor="middle"
            fontSize={lastNameFontSize}
            fontWeight="bold"
            fill={effectiveTextColor}
            stroke={actualTextOutline}
            strokeWidth={textStrokeWidth}
            paintOrder="stroke"
            fontFamily={effectiveFontFamily}
            letterSpacing="4"
          >
            {lastNameText}
          </text>
        </>
      )}
      
      {/* Jersey number */}
      {number !== '' && (
        <>
          {/* Outer stroke (if enabled) */}
          {numberStrokeWidth2 > 0 && actualNumberOutline2 && (
            <text
              x={centerX}
              y={calculatedNumberY}
              textAnchor="middle"
              fontSize={numberFontSize}
              fontWeight="bold"
              fill="transparent"
              stroke={actualNumberOutline2}
              strokeWidth={totalNumberStroke}
              strokeLinejoin="round"
              fontFamily={effectiveFontFamily}
            >
              {numStr}
            </text>
          )}
          {/* Inner stroke + fill */}
          <text
            x={centerX}
            y={calculatedNumberY}
            textAnchor="middle"
            fontSize={numberFontSize}
            fontWeight="bold"
            fill={effectiveNumberColor}
            stroke={actualNumberOutline}
            strokeWidth={numberStrokeWidth}
            paintOrder="stroke"
            fontFamily={effectiveFontFamily}
          >
            {numStr}
          </text>
        </>
      )}
    </svg>
  );
};

// Team color presets (for quick selection)
export const TeamPresets = {
  Lakers: { fillColor: '#552583', outlineColor: '#FDB927', numberColor: '#FDB927', numberOutline: '#552583' },
  Celtics: { fillColor: '#007A33', outlineColor: '#BA9653', numberColor: '#ffffff', numberOutline: '#007A33' },
  Bulls: { fillColor: '#CE1141', outlineColor: '#000000', numberColor: '#ffffff', numberOutline: '#000000' },
  Warriors: { fillColor: '#1D428A', outlineColor: '#FFC72C', numberColor: '#FFC72C', numberOutline: '#1D428A' },
  Heat: { fillColor: '#98002E', outlineColor: '#F9A01B', numberColor: '#ffffff', numberOutline: '#000000' },
  Knicks: { fillColor: '#006BB6', outlineColor: '#F58426', numberColor: '#ffffff', numberOutline: '#006BB6' },
};

// Default view settings (for front and back independence)
export const DEFAULT_VIEW_SETTINGS = {
  numberY: 55,
  numberSize: 100,
  numberColor: '#ffffff',
  numberOutline: '#1e40af',
  numberOutline2: null,
  numberBorderWeight: 6,
  numberBorderWeight2: 0,
  textY: 5,
  textSize: 100,
  textColor: '#ffffff',
  textOutline: '#1e40af',
  textOutline2: null,
  textBorderWeight: 4,
  textBorderWeight2: 0,
  fontFamily: 'Impact, Haettenschweiler, sans-serif'
};

// Default jersey template with all new fields
export const DEFAULT_JERSEY_TEMPLATE = {
  id: 'default',
  name: 'Home',
  fillColor: '#2563eb',
  outlineColor: '#1e40af',
  // Legacy props (for backward compatibility)
  numberColor: '#ffffff',
  numberOutline: '#1e40af',
  numberOutline2: null,
  textColor: '#ffffff',
  textOutline: '#1e40af',
  textOutline2: null,
  fontFamily: 'Impact, Haettenschweiler, sans-serif',
  logoUrl: null,
  // Logo positioning controls
  logoY: 65,          // 0-100 percent from top
  logoSize: 50,       // 10-100 percent scale
  logoRotation: 0,    // -180 to 180 degrees
  logoFlipX: false,   // horizontal flip
  logoFlipY: false,   // vertical flip
  numberY: 55,
  nameY: 5,
  numberSize: 100,
  nameSize: 100,
  jerseyBorderWeight: 12,
  numberBorderWeight: 6,
  numberBorderWeight2: 0,
  nameBorderWeight: 4,
  nameBorderWeight2: 0,
  // NEW: Front/Back independent settings (null = use legacy props)
  front: null,
  back: null,
  // Team color palette (up to 6 colors)
  colorPalette: [],
  // Default jersey flag
  isDefault: false
};

// Helper to create view settings from legacy template
export const createViewSettings = (template, forFront = true) => ({
  numberY: forFront ? (template.front?.numberY ?? template.numberY ?? 55) : (template.back?.numberY ?? template.numberY ?? 55),
  numberSize: forFront ? (template.front?.numberSize ?? template.numberSize ?? 100) : (template.back?.numberSize ?? template.numberSize ?? 100),
  numberColor: forFront ? (template.front?.numberColor ?? template.numberColor) : (template.back?.numberColor ?? template.numberColor),
  numberOutline: forFront ? (template.front?.numberOutline ?? template.numberOutline) : (template.back?.numberOutline ?? template.numberOutline),
  numberOutline2: forFront ? (template.front?.numberOutline2 ?? template.numberOutline2) : (template.back?.numberOutline2 ?? template.numberOutline2),
  numberBorderWeight: forFront ? (template.front?.numberBorderWeight ?? template.numberBorderWeight ?? 6) : (template.back?.numberBorderWeight ?? template.numberBorderWeight ?? 6),
  numberBorderWeight2: forFront ? (template.front?.numberBorderWeight2 ?? template.numberBorderWeight2 ?? 0) : (template.back?.numberBorderWeight2 ?? template.numberBorderWeight2 ?? 0),
  textY: forFront ? (template.front?.textY ?? template.nameY ?? 5) : (template.back?.textY ?? template.nameY ?? 5),
  textSize: forFront ? (template.front?.textSize ?? template.nameSize ?? 100) : (template.back?.textSize ?? template.nameSize ?? 100),
  textColor: forFront ? (template.front?.textColor ?? template.textColor ?? template.numberColor) : (template.back?.textColor ?? template.textColor ?? template.numberColor),
  textOutline: forFront ? (template.front?.textOutline ?? template.textOutline ?? template.numberOutline) : (template.back?.textOutline ?? template.textOutline ?? template.numberOutline),
  textOutline2: forFront ? (template.front?.textOutline2 ?? template.textOutline2) : (template.back?.textOutline2 ?? template.textOutline2),
  textBorderWeight: forFront ? (template.front?.textBorderWeight ?? template.nameBorderWeight ?? 4) : (template.back?.textBorderWeight ?? template.nameBorderWeight ?? 4),
  textBorderWeight2: forFront ? (template.front?.textBorderWeight2 ?? template.nameBorderWeight2 ?? 0) : (template.back?.textBorderWeight2 ?? template.nameBorderWeight2 ?? 0),
  fontFamily: forFront ? (template.front?.fontFamily ?? template.fontFamily) : (template.back?.fontFamily ?? template.fontFamily)
});

export default JerseyIcon;
